import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { providePrimeNG } from 'primeng/config';
import { getKDSPrimengThemePreset } from '../presets/primeng-theme-preset';
import { themeAttribute } from '../constants/theme-attribute.const';
import { KDSPrimengThemePresetColorOverrides } from '../models/primeng-theme-preset.model';

/**
 * Provides the KDS theme system for an Angular application.
 *
 * Configures PrimeNG with the KDS preset using `[data-theme="dark"]` as the dark mode selector.
 * Use {@link ThemeService.applyTheme} to switch between `'light'` and `'dark'` at runtime.
 *
 * @param presetColorOverrides - Optional deep-partial color-scheme overrides applied on top of the KDS base preset.
 * @returns Angular `EnvironmentProviders` to pass to `bootstrapApplication` or a route's `providers`.
 *
 * @example Without overrides
 * ```ts
 * bootstrapApplication(AppComponent, {
 *   providers: [provideKDSTheme()],
 * });
 * ```
 *
 * @example With custom color token overrides
 * ```ts
 * provideKDSTheme({ light: { kds: { filterTagBackground: 'var(--custom-tag)' } } })
 * ```
 */
export const provideKDSTheme = (presetColorOverrides?: KDSPrimengThemePresetColorOverrides): EnvironmentProviders =>
  makeEnvironmentProviders([
    providePrimeNG({
      theme: {
        preset: getKDSPrimengThemePreset(presetColorOverrides),
        options: {
          darkModeSelector: `[${themeAttribute}="dark"]`,
        },
      },
    }),
  ]);
